import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useEffect, useMemo, useRef} from 'react'
import {ReactComponent as FaDoctor} from '../../assets/icons/FaDoctor.svg'
import {ReactComponent as FaThermometerHalf} from '../../assets/icons/FaThermometerHalf.svg'
import {ReactComponent as MdOutlineAssignmentTurnedIn} from '../../assets/icons/MdOutlineAssignmentTurnedIn.svg'
import {ReactComponent as AssignPathway} from '../../assets/menu/AssignPathway.svg'
import {ReactComponent as Filter} from '../../assets/menu/Filter.svg'

import TranslateUtils from '../../i18n/TranslateUtils'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterModeChangeListener,
    removeFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {
    onItemTableToolBarSelectedListener,
    tableToolBarEventTypes,
} from './broadcast/TableToolBarEvent'
import {tableToolBarDescriptionList} from './Constant'
import {useDatatableContext} from './DataTableContext'
import StyledAdvancedFilter from './StyledAdvancedFilter'
import StyledFilterByHealthCarePractitioner from './StyledFilterByHealthCarePractitioner'
import StyledFilterByPathway from './StyledFilterByPathway'
import StyledFilterBySicknessLevel from './StyledFilterBySicknessLevel'
import StyledFilterByStaticList from './StyledFilterByStaticList'
import StyledToolbarIcon from './StyledToolbarIcon'

const StyledFilterToolBar = () => {
    useSignals()

    const {tableType, initialAdvancedFilterRequest, handleClearFilterRequest} =
        useDatatableContext()
    const {translateDatatable} = TranslateUtils()

    const filterByStaticListRef = useRef(null)
    const filterByPathwayRef = useRef(null)
    const filterBySicknessLevelRef = useRef(null)
    const filterByHealthCarePractitionerRef = useRef(null)
    const filterByAdvancedRef = useRef(null)

    const clearFilterRef = useRef(null)
    const hasFilter = useSignal(
        initialAdvancedFilterRequest &&
            Object.keys(initialAdvancedFilterRequest).length > 0,
    )

    const handleClearAllFilter = () => {
        onFilterModeChangeListener(
            tableType,
            filterExplainEventTypes.CLEAR_ALL_FILTER,
        )

        if (handleClearFilterRequest) {
            handleClearFilterRequest()
        }

        window.history.replaceState(null, 'TaVIE', `/patients`)
    }

    const query = useQueryParams()
    //#region URL Query Params
    const hasQueryParamsFilter = useMemo(() => {
        return query.get('staticListId') || query.get('pathwayId')
    }, [query])
    //#endregion

    useEffect(() => {
        if (
            clearFilterRef.current &&
            initialAdvancedFilterRequest &&
            Object.keys(initialAdvancedFilterRequest).length < 1
        ) {
            if (!hasQueryParamsFilter) {
                clearFilterRef.current?.classList?.add('hidden')
            }
        }

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.HAS_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.HAS_FILTER) {
                    hasFilter.value = true
                    if (clearFilterRef.current) {
                        clearFilterRef.current?.classList?.remove('hidden')
                    }
                }
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.CLEAR_ALL_FILTER) {
                    hasFilter.value = false
                    if (clearFilterRef.current) {
                        clearFilterRef.current?.classList?.add('hidden')
                    }
                }
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.HAS_FILTER,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )
        }
    }, [])

    return (
        <div className="container">
            <div className="flex align-items-center justify-content-between px-2 mb-0">
                <h6 className="toggle-group-label font-bold m-0 mb-1 ">
                    {translateDatatable('Filter')}
                </h6>
                <div
                    ref={clearFilterRef}
                    className="cursor-pointer text-gray-600 text-danger"
                    onClick={() => {
                        handleClearAllFilter()
                    }}>
                    <i className="pi pi-times mr-1"></i>
                    <span>Clear</span>
                </div>
            </div>
            <div className="custom-toolbar-items-wrapper py-0 pr-2">
                <StyledToolbarIcon
                    icon={<MdOutlineAssignmentTurnedIn />}
                    label={translateDatatable('Static List')} //Filter by
                    description={tableToolBarDescriptionList.filterByStaticList}
                    onClick={() => {
                        filterByStaticListRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Static List',
                        )
                    }}
                />
                <StyledToolbarIcon
                    icon={<AssignPathway />}
                    label={translateDatatable('Pathway')}
                    description={tableToolBarDescriptionList.filterByPathway}
                    onClick={() => {
                        filterByPathwayRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Pathway',
                        )
                    }}
                />

                <StyledToolbarIcon
                    icon={<FaThermometerHalf width={23} height={23} />}
                    label={translateDatatable('Sickness Level')}
                    description={tableToolBarDescriptionList.filterBySickness}
                    onClick={() => {
                        filterBySicknessLevelRef.current.showForm()
                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Sickness Level',
                        )
                    }}
                />

                <StyledToolbarIcon
                    icon={<FaDoctor />}
                    label={translateDatatable('HC.Practitioner')}
                    description={tableToolBarDescriptionList.filterByHCP}
                    onClick={() => {
                        filterByHealthCarePractitionerRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'HC.Practitioner',
                        )
                    }}
                />
                <StyledToolbarIcon
                    icon={<Filter />}
                    label={translateDatatable('Advanced Filter')}
                    description={tableToolBarDescriptionList.advanceFilter}
                    onClick={() => {
                        filterByAdvancedRef.current.showForm()
                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Advanced Filter',
                        )
                    }}
                />
            </div>

            <hr className="toggle-toolbar-divider" />
            <StyledFilterByStaticList ref={filterByStaticListRef} />

            <StyledFilterByPathway ref={filterByPathwayRef} />

            <StyledFilterBySicknessLevel ref={filterBySicknessLevelRef} />

            <StyledFilterByHealthCarePractitioner
                ref={filterByHealthCarePractitionerRef}
            />

            <StyledAdvancedFilter ref={filterByAdvancedRef} />
        </div>
    )
}

export default StyledFilterToolBar
